/**
 * Store Pinia pour la gestion des notifications de messages non lus
 */

import { defineStore } from 'pinia'
import axios from 'axios'

interface NotificationState {
  unreadMessagesCount: number
  unreadByConversation: Record<number, number>
  isLoading: boolean
  lastFetchTime: number | null
}

export const useNotificationStore = defineStore('notification', {
  state: (): NotificationState => ({
    unreadMessagesCount: 0,
    unreadByConversation: {},
    isLoading: false,
    lastFetchTime: null
  }),

  getters: {
    /**
     * Obtenir le nombre de messages non lus pour une conversation
     */
    getUnreadForConversation: (state) => (conversationId: number): number => {
      return state.unreadByConversation[conversationId] || 0
    },

    /**
     * Vérifier s'il y a des messages non lus
     */
    hasUnreadMessages: (state): boolean => {
      return state.unreadMessagesCount > 0
    }
  },

  actions: {
    /**
     * Récupérer le nombre de messages non lus depuis l'API
     */
    async fetchUnreadCount(): Promise<void> {
      // Éviter les appels trop fréquents (minimum 5 secondes entre chaque)
      const now = Date.now()
      if (this.lastFetchTime && now - this.lastFetchTime < 5000) {
        return
      }

      this.isLoading = true
      try {
        const response = await axios.get('/backoffice/chats/unread-count')
        
        if (response.data.status === 0) {
          this.unreadMessagesCount = response.data.data.total
          this.unreadByConversation = response.data.data.byConversation
          this.lastFetchTime = now
          
          console.log('[NotificationStore] Messages non lus:', {
            total: this.unreadMessagesCount,
            byConversation: this.unreadByConversation
          })
        }
      } catch (error) {
        console.error('[NotificationStore] Erreur lors de la récupération des messages non lus:', error)
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Incrémenter le compteur de messages non lus (appelé via Socket.IO)
     */
    incrementUnread(conversationId: number): void {
      console.log('[NotificationStore] 🔔 incrementUnread appelé pour conversation:', conversationId)
      console.log('[NotificationStore] État avant:', {
        total: this.unreadMessagesCount,
        byConversation: { ...this.unreadByConversation }
      })
      
      // Incrémenter le total
      this.unreadMessagesCount++
      
      // Créer un nouvel objet pour garantir la réactivité Vue/Pinia
      const updatedByConversation = { ...this.unreadByConversation }
      
      if (!updatedByConversation[conversationId]) {
        updatedByConversation[conversationId] = 0
      }
      updatedByConversation[conversationId]++
      
      // Assigner le nouvel objet pour déclencher la réactivité
      this.unreadByConversation = updatedByConversation
      
      console.log('[NotificationStore] ✅ Nouveau message non lu:', {
        conversationId,
        total: this.unreadMessagesCount,
        byConversation: { ...this.unreadByConversation }
      })
    },

    /**
     * Marquer les messages d'une conversation comme lus
     */
    async markConversationRead(conversationId: number): Promise<void> {
      try {
        const response = await axios.post(`/backoffice/chats/${conversationId}/mark-read`)
        
        if (response.data.status === 0) {
          const markedCount = response.data.data.marked_count
          
          // Mettre à jour les compteurs locaux
          const previousCount = this.unreadByConversation[conversationId] || 0
          this.unreadMessagesCount = Math.max(0, this.unreadMessagesCount - previousCount)
          delete this.unreadByConversation[conversationId]
          
          console.log('[NotificationStore] Messages marqués comme lus:', {
            conversationId,
            markedCount,
            remainingTotal: this.unreadMessagesCount
          })
        }
      } catch (error) {
        console.error('[NotificationStore] Erreur lors du marquage comme lu:', error)
      }
    },

    /**
     * Gérer la notification Socket.IO de messages lus
     */
    handleMessagesRead(conversationId: number): void {
      const previousCount = this.unreadByConversation[conversationId] || 0
      this.unreadMessagesCount = Math.max(0, this.unreadMessagesCount - previousCount)
      delete this.unreadByConversation[conversationId]
      
      console.log('[NotificationStore] Messages marqués comme lus (via Socket):', {
        conversationId,
        remainingTotal: this.unreadMessagesCount
      })
    },

    /**
     * Réinitialiser le store
     */
    reset(): void {
      this.unreadMessagesCount = 0
      this.unreadByConversation = {}
      this.isLoading = false
      this.lastFetchTime = null
    }
  }
})
